import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CloseIcon from '@mui/icons-material/Close'
import { useContext, useEffect, useMemo, useState } from 'react'
import { loadStatus } from '../constants/loadStatus'
import { errorMsg } from '../constants/messages'
import {
  checkboxPrimarySx,
  dialogCancelButtonSx,
  dialogLoadingOverlaySx,
  dialogSaveButtonSx,
  dropdownBackdropSx,
  selectableRowSx,
  uiPalette,
  dropdownMenuSx,
  dialogActionsSx,
  dropdownSearchInputSx,
  dropdownFieldsetSx,
} from '../constants/uiSx'
import { LanguageContext } from '../util/LanguageContext'
import { ModalConfirmDialogDCP } from './ModalConfirmDialogDCP'
import { useCG01RoleGet } from '../hooks/useCG01RoleGet'
import { useCG02RoleAdd } from '../hooks/useCG02RoleAdd'
import { useCG03RoleUpdate } from '../hooks/useCG03RoleUpdate'
import { useCG04RoleDelete } from '../hooks/useCG04RoleDelete'
import { useDCPUserListGet, type DcpUserInfo } from '../hooks/useDCPUserListGet'

interface OperationPermissionDialogProps {
  open: boolean
  tenant: string
  onClose: () => void
  onSave: () => Promise<void>
}

const formatUserLabel = (user: DcpUserInfo) => {
  const lastName = user.lastName?.trim() ?? ''
  const firstName = user.firstName?.trim() ?? ''
  const username = user.username?.trim() ?? ''
  const fullName = [lastName, firstName].filter(Boolean).join(', ')

  if (!fullName) {
    return username
  }

  return `${fullName} (${username})`
}

const compareUsers = (left: DcpUserInfo, right: DcpUserInfo) =>
  formatUserLabel(left).localeCompare(formatUserLabel(right), 'ja')

export const OperationPermissionDialog: React.FC<OperationPermissionDialogProps> = ({
  open,
  tenant,
  onClose,
  onSave,
}) => {
  const translation = useContext(LanguageContext)

  const dcpUserListGet = useDCPUserListGet()
  const cg01RoleGet = useCG01RoleGet()
  const cg02RoleAdd = useCG02RoleAdd()
  const cg03RoleUpdate = useCG03RoleUpdate()
  const cg04RoleDelete = useCG04RoleDelete()

  const [adminUsers, setAdminUsers] = useState<DcpUserInfo[]>([])
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [initialSelectedUserIds, setInitialSelectedUserIds] = useState<string[]>([])
  const [searchKeyword, setSearchKeyword] = useState('')
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const [errorDialog, setErrorDialog] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const loading =
    dcpUserListGet.loading === loadStatus.LOADING ||
    cg01RoleGet.loading === loadStatus.LOADING ||
    cg02RoleAdd.loading === loadStatus.LOADING ||
    cg03RoleUpdate.loading === loadStatus.LOADING ||
    cg04RoleDelete.loading === loadStatus.LOADING

  const selectedSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds])

  const selectedUsers = useMemo(
    () => adminUsers.filter(user => selectedSet.has(user.id)).sort(compareUsers),
    [adminUsers, selectedSet],
  )

  const filteredUsers = useMemo(() => {
    const normalizedKeyword = searchKeyword.trim().toLowerCase()
    const sortedUsers = [...adminUsers].sort(compareUsers)

    if (normalizedKeyword === '') {
      return sortedUsers
    }

    return sortedUsers.filter(user => {
      const username = user.username?.toLowerCase() ?? ''
      const firstName = user.firstName?.toLowerCase() ?? ''
      const lastName = user.lastName?.toLowerCase() ?? ''
      const label = formatUserLabel(user).toLowerCase()

      return (
        username.includes(normalizedKeyword) ||
        firstName.includes(normalizedKeyword) ||
        lastName.includes(normalizedKeyword) ||
        label.includes(normalizedKeyword)
      )
    })
  }, [adminUsers, searchKeyword])

  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every(user => selectedSet.has(user.id))

  const someFilteredSelected =
    filteredUsers.some(user => selectedSet.has(user.id)) && !allFilteredSelected

  const isSelectionChanged = useMemo(() => {
    if (selectedUserIds.length !== initialSelectedUserIds.length) {
      return true
    }

    const initialSet = new Set(initialSelectedUserIds)
    return selectedUserIds.some(id => !initialSet.has(id))
  }, [selectedUserIds, initialSelectedUserIds])

  const canSave = isSelectionChanged && !isSaving && !loading

  // ユーザー一覧を取得
  useEffect(() => {
    if (!open) {
      setSearchKeyword('')
      setUserDropdownOpen(false)
      return
    }

    const fetchDialogData = async () => {
      const roleResult = await cg01RoleGet.execRoleGet(tenant, 'representative')
      if (!roleResult.success) {
        setErrorDialog(roleResult.message ?? errorMsg.SYSTEM_ERROR)
        return
      }

      // 全ユーザーを取得（ページング無しで全て取得）
      let start = 0
      let allUsersList: DcpUserInfo[] = []
      let hasMore = true

      while (hasMore) {
        const result = await dcpUserListGet.execUserListGet(start, 100, '')
        if (result.success && result.info) {
          allUsersList = [...allUsersList, ...result.info.list]
          hasMore = allUsersList.length < result.info.count
          start = allUsersList.length
        } else {
          setErrorDialog(result.message ?? errorMsg.SYSTEM_ERROR)
          return
        }
      }

      const administrators = allUsersList
        .filter(user => user.role === 'administrator')
        .sort(compareUsers)
      const representativeIds = roleResult.info?.representative ?? []

      setAdminUsers(administrators)
      setSelectedUserIds(representativeIds)
      setInitialSelectedUserIds(representativeIds)
    }

    fetchDialogData()
  }, [open, tenant])

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId],
    )
  }

  const handleToggleAllFiltered = () => {
    const visibleIds = filteredUsers.map(user => user.id)
    if (allFilteredSelected) {
      setSelectedUserIds(prev => prev.filter(id => !visibleIds.includes(id)))
      return
    }

    setSelectedUserIds(prev => {
      const set = new Set(prev)
      visibleIds.forEach(id => set.add(id))
      return Array.from(set)
    })
  }

  const handleSave = async () => {
    if (!isSelectionChanged) {
      return
    }

    try {
      setIsSaving(true)

      const normalizedIds = [...selectedUserIds].sort()
      const hadRepresentative = initialSelectedUserIds.length > 0

      let result: { success: boolean; message?: string }
      if (normalizedIds.length === 0) {
        result = hadRepresentative
          ? await cg04RoleDelete.execRoleDelete(tenant, 'representative')
          : { success: true }
      } else if (!hadRepresentative) {
        result = await cg02RoleAdd.execRoleAdd(tenant, normalizedIds)
      } else {
        result = await cg03RoleUpdate.execRoleUpdate(tenant, normalizedIds)
      }

      if (!result.success) {
        setErrorDialog(result.message ?? errorMsg.SYSTEM_ERROR)
        return
      }

      await onSave()
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      {errorDialog && (
        <ModalConfirmDialogDCP
          open={!!errorDialog}
          text={errorDialog}
          onClick={() => setErrorDialog(null)}
          buttonText='button_close'
        />
      )}

      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (userDropdownOpen && reason === 'backdropClick') {
            return
          }
          onClose()
        }}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ mt: 2, fontSize: '1.5rem', fontWeight: 700, textAlign: 'center', flexShrink: 0 }}>
          {translation['label_operation_permission_title']}
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, md: 4 },
            position: 'relative',
            flex: '1 1 auto',
            height: 600,
            overflowY: 'auto',
          }}
        >
          <Typography sx={{ mt: 1, mb: 5, mr: 2, ml: 2, fontSize: '1rem', textAlign: 'center', color: '#666' }}>
            {translation['message_operation_permission_description']}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Box
                component='fieldset'
                sx={dropdownFieldsetSx(userDropdownOpen)}
                onClick={() => setUserDropdownOpen(true)}
              >
                {selectedUsers.map(user => (
                  <Chip
                    key={user.id}
                    label={formatUserLabel(user)}
                    onDelete={e => {
                      e.stopPropagation()
                      handleToggleUser(user.id)
                    }}
                    size='small'
                    sx={{
                      height: 'auto',
                      maxWidth: '250px',
                      '& .MuiChip-label': {
                        px: 1,
                        py: 0.5,
                      },
                    }}
                  />
                ))}

                <TextField
                  value={searchKeyword}
                  onChange={e => setSearchKeyword(e.target.value)}
                  onFocus={() => setUserDropdownOpen(true)}
                  placeholder={translation['label_new_user']}
                  variant='standard'
                  size='small'
                  InputProps={{ disableUnderline: true }}
                  sx={dropdownSearchInputSx(userDropdownOpen)}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', color: 'rgba(0,0,0,0.54)' }}>
                  {selectedUserIds.length > 0 && (
                    <IconButton
                      size='small'
                      onClick={e => {
                        e.stopPropagation()
                        setSelectedUserIds([])
                      }}
                      sx={{ mr: 0.25 }}
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  )}
                  {userDropdownOpen
                    ? <KeyboardArrowUpIcon fontSize='small' />
                    : <KeyboardArrowDownIcon fontSize='small' />}
                </Box>
              </Box>

                {/* ドロップダウンテーブル */}
              {userDropdownOpen && (
                <Box
                  sx={dropdownMenuSx(410)}
                >
                  <Box
                    onClick={handleToggleAllFiltered}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: allFilteredSelected ? uiPalette.selectedBg : 'transparent',
                      '&:hover': {
                        backgroundColor: allFilteredSelected ? uiPalette.selectedBg : uiPalette.hoverBg,
                      },
                    }}
                  >
                    <Checkbox
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected}
                      onChange={handleToggleAllFiltered}
                      onClick={e => e.stopPropagation()}
                      size='small'
                      sx={checkboxPrimarySx}
                    />
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      {translation['label_select_all']}
                    </Typography>
                  </Box>

                  {filteredUsers.map(user => (
                    <Box
                      key={user.id}
                      onClick={() => handleToggleUser(user.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        fontSize: '0.875rem',
                        ...selectableRowSx(selectedSet.has(user.id)),
                      }}
                    >
                      <Checkbox
                        checked={selectedSet.has(user.id)}
                        onChange={() => handleToggleUser(user.id)}
                        onClick={e => e.stopPropagation()}
                        size='small'
                        sx={checkboxPrimarySx}
                      />
                      <Typography sx={{ fontSize: '0.875rem' }}>
                        {formatUserLabel(user)}
                      </Typography>
                    </Box>
                  ))}

                  {/* {filteredUsers.length === 0 && (
                    <Box sx={{ textAlign: 'center', color: '#888', py: 4, fontSize: '0.875rem' }}>
                      {translation['message_target_user_not_found']}
                    </Box>
                  )} */}
                </Box>
              )}
            </Box>

            {userDropdownOpen && (
              <Box
                sx={dropdownBackdropSx}
                onClick={e => {
                  if (e.currentTarget === e.target) {
                    setUserDropdownOpen(false)
                  }
                }}
              />
            )}
          </Box>

          {(loading || isSaving) && (
            <Box
              sx={dialogLoadingOverlaySx}
            >
              <CircularProgress size={40} />
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={dialogActionsSx()}
        >
          <Button
            onClick={onClose}
            sx={dialogCancelButtonSx}
            disabled={loading || isSaving}
          >
            {translation['button_cancel']}
          </Button>
          <Button
            onClick={handleSave}
            variant='contained'
            disabled={loading || isSaving || !isSelectionChanged}
            sx={dialogSaveButtonSx(canSave)}
          >
            {translation['button_save']}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
